import React, { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Plus, DotsThreeVertical, Trash, PencilSimple, Key, Prohibit,
  CheckCircle, Copy, X, UsersThree, Buildings, Gear, Bell,
} from "@phosphor-icons/react";

const inp = "w-full h-9 px-3 border border-border-control bg-white text-[13.5px] rounded-[8px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none focus:border-equipr-black";
const lbl = "text-[10.5px] font-bold uppercase tracking-[0.1em] text-ink-muted mb-1.5 block";

const ROLE_BADGE = {
  admin: "bg-equipr-yellow/30 text-equipr-black",
  scheduler: "bg-blue-100 text-blue-700",
  viewer: "bg-gray-200 text-gray-600",
};
const STATUS_BADGE = {
  active: "bg-green-100 text-green-700",
  invited: "bg-amber-100 text-amber-700",
  inactive: "bg-red-100 text-red-700",
};

function fmt(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return "—"; }
}

/* ------------------------------ Link modal -------------------------------- */
function LinkModal({ title, link, emailSent, email, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {}
  };
  return (
    <div className="fixed inset-0 z-[60] bg-[rgba(26,26,26,0.34)] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-surface-panel border border-border-warm rounded-[14px] overflow-hidden shadow-modal w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="link-modal">
        <div className="flex items-center justify-between px-5 py-3 bg-equipr-black text-white">
          <h3 className="font-bold text-sm">{title}</h3>
          <button onClick={onClose} data-testid="link-modal-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          {emailSent ? (
            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 px-3 py-2">
              <CheckCircle size={18} weight="fill" /> Email sent to <b>{email}</b>
            </div>
          ) : (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2">
              Email delivery isn't configured yet. Copy this link and send it to the user manually.
            </div>
          )}
          <div>
            <label className={lbl}>Secure link (expires in 48h)</label>
            <div className="flex gap-2">
              <input readOnly value={link} className={inp + " text-xs bg-gray-50"} data-testid="link-modal-input" onFocus={(e) => e.target.select()} />
              <button onClick={copy} data-testid="link-modal-copy"
                className="h-9 px-3 bg-equipr-yellow text-equipr-black text-xs font-bold rounded-none hover:bg-equipr-yellow-dark flex items-center gap-1 whitespace-nowrap">
                <Copy size={14} weight="bold" /> {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
        </div>
        <div className="flex justify-end px-5 py-3 border-t border-gray-200">
          <button onClick={onClose} className="h-9 px-4 bg-equipr-black text-white text-sm font-semibold rounded-none hover:bg-gray-800">Done</button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------- Invite / edit modal ------------------------- */
function UserModal({ mode, user, projects = [], onClose, onDone, onLink }) {
  const [form, setForm] = useState({
    email: user?.email || "", name: user?.name || "", role: user?.role || "viewer",
    project_access: user?.project_access || "all",
    allowed_projects: user?.allowed_projects || [],
    resource_scope: user?.resource_scope || "both",
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const isAdminRole = form.role === "admin";

  const toggleProject = (id) => setForm((s) => ({
    ...s,
    allowed_projects: s.allowed_projects.includes(id)
      ? s.allowed_projects.filter((x) => x !== id)
      : [...s.allowed_projects, id],
  }));

  const submit = async () => {
    setError(""); setSaving(true);
    try {
      const access = {
        role: form.role,
        project_access: isAdminRole ? "all" : form.project_access,
        allowed_projects: (!isAdminRole && form.project_access === "restricted") ? form.allowed_projects : [],
        resource_scope: isAdminRole ? "both" : form.resource_scope,
      };
      if (mode === "invite") {
        const { data } = await api.post("/users/invite", { email: form.email, name: form.name, ...access });
        onDone();
        onLink({ title: "User invited", link: data.invite_link, emailSent: data.email_sent, email: data.user.email });
      } else {
        await api.put(`/users/${user.id}`, { name: form.name, ...access });
        onDone();
      }
      onClose();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong.");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-[rgba(26,26,26,0.34)] flex justify-end eq-sheet-overlay" onClick={onClose}>
      <div className="bg-surface-panel shadow-drawer border-l border-border-warm w-full max-w-[800px] h-full flex flex-col eq-sheet" onClick={(e) => e.stopPropagation()} data-testid="user-modal">
        <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-divider-inner shrink-0">
          <div>
            <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-ink-muted">{mode === "invite" ? "New User" : "Edit User"}</div>
            <h3 className="font-bold text-[20px] text-ink-primary leading-tight mt-0.5">{mode === "invite" ? "Invite User" : (user?.name || "Edit User")}</h3>
          </div>
          <button onClick={onClose} data-testid="user-modal-close" className="w-9 h-9 flex items-center justify-center rounded-[9px] border border-border-control text-ink-secondary hover:bg-surface-panel"><X size={16} weight="bold" /></button>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto">
          {error && <div className="border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2" data-testid="user-modal-error">{error}</div>}
          <div>
            <label className={lbl}>Email {mode === "invite" ? "*" : ""}</label>
            <input type="email" className={inp + (mode === "edit" ? " bg-gray-100 text-gray-500" : "")} value={form.email}
              disabled={mode === "edit"} onChange={(e) => set("email", e.target.value)} data-testid="user-email-input" placeholder="user@company.com" />
          </div>
          <div>
            <label className={lbl}>Full Name *</label>
            <input className={inp} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="user-name-input" placeholder="Jane Smith" />
          </div>
          <div>
            <label className={lbl}>Role *</label>
            <select className={inp} value={form.role} onChange={(e) => set("role", e.target.value)} data-testid="user-role-select">
              <option value="admin">Admin — full access & settings</option>
              <option value="scheduler">Scheduler — can edit schedules</option>
              <option value="viewer">Viewer — read only</option>
            </select>
          </div>

          {isAdminRole ? (
            <div className="text-[11px] text-gray-500 bg-gray-50 border border-gray-200 px-3 py-2" data-testid="user-admin-access-note">
              Admins always have access to <b>all projects</b> and <b>both Assets & Workers</b>.
            </div>
          ) : (
            <>
              <div>
                <label className={lbl}>Resource scope</label>
                <select className={inp} value={form.resource_scope} onChange={(e) => set("resource_scope", e.target.value)} data-testid="user-resource-scope-select">
                  <option value="both">Both — Assets & Workers</option>
                  <option value="assets">Assets only</option>
                  <option value="workers">Workers only</option>
                </select>
              </div>
              <div>
                <label className={lbl}>Project access</label>
                <select className={inp} value={form.project_access} onChange={(e) => set("project_access", e.target.value)} data-testid="user-project-access-select">
                  <option value="all">All projects</option>
                  <option value="restricted">Specific projects only</option>
                </select>
              </div>
              {form.project_access === "restricted" && (
                <div className="border border-gray-200" data-testid="user-project-list">
                  <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-200 text-[11px] font-bold uppercase tracking-wider text-gray-500 flex items-center justify-between">
                    <span>Allowed projects</span>
                    <span className="text-gray-400 normal-case tracking-normal">{form.allowed_projects.length} selected</span>
                  </div>
                  <div className="max-h-44 overflow-y-auto p-1">
                    {projects.length === 0 ? (
                      <p className="text-xs text-gray-400 px-2 py-2">No projects available.</p>
                    ) : projects.map((p) => (
                      <label key={p.id} className="flex items-center gap-2 px-2 py-1.5 text-[13px] cursor-pointer hover:bg-gray-50">
                        <input type="checkbox" checked={form.allowed_projects.includes(p.id)}
                          onChange={() => toggleProject(p.id)} data-testid={`user-project-option-${p.id}`} />
                        <span className="truncate">{p.project_name || p.site_address}</span>
                        <span className="ml-auto text-[10px] text-gray-400">#{p.project_no}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {mode === "invite" && (
            <p className="text-[11px] text-gray-500">The user gets a secure link to set their own password and activate the account.</p>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 bg-surface-panel border-t border-divider-inner shrink-0">
          <button onClick={onClose} className="h-9 px-4 border border-border-control bg-white text-equipr-black text-[12px] uppercase tracking-[0.04em] font-semibold rounded-[7px] hover:bg-surface-panel active:bg-surface-pressed">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="user-modal-confirm"
            className="h-9 px-5 bg-equipr-yellow text-equipr-black text-[12px] uppercase tracking-[0.04em] font-bold rounded-[7px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px disabled:opacity-60">
            {saving ? "Saving…" : mode === "invite" ? "Send Invite" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------ Confirm modal ----------------------------- */
function ConfirmModal({ title, message, confirmLabel, danger, onCancel, onConfirm }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const go = async () => {
    setBusy(true); setError("");
    try { await onConfirm(); } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || "Action failed."); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-[rgba(26,26,26,0.34)] flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-surface-panel border border-border-warm rounded-[14px] overflow-hidden shadow-modal w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="confirm-modal">
        <div className="px-5 py-4">
          <h3 className="font-bold text-sm text-equipr-black mb-1">{title}</h3>
          <p className="text-sm text-gray-600">{message}</p>
          {error && <div className="mt-3 border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2" data-testid="confirm-error">{error}</div>}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200">
          <button onClick={onCancel} className="h-9 px-4 border border-border-control bg-white text-equipr-black text-[12px] uppercase tracking-[0.04em] font-semibold rounded-[7px] hover:bg-surface-panel active:bg-surface-pressed">Cancel</button>
          <button onClick={go} disabled={busy} data-testid="confirm-ok"
            className={`h-9 px-5 text-sm font-bold rounded-none disabled:opacity-60 ${danger ? "bg-red-600 text-white hover:bg-red-700" : "bg-equipr-yellow text-equipr-black hover:bg-equipr-yellow-dark"}`}>
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------- Row menu --------------------------------- */
function UserRow({ u, self, onEdit, onReset, onStatus, onDelete }) {
  const [menu, setMenu] = useState(false);
  const isSelf = u.id === self?.id;
  return (
    <tr className="border-b border-divider-row hover:bg-surface-panel group" data-testid={`user-row-${u.id}`}>
      <td className="px-4 py-2.5">
        <div className="font-semibold text-equipr-black text-sm">{u.name || "—"}{isSelf && <span className="ml-2 text-[10px] text-gray-400 font-normal">(you)</span>}</div>
        <div className="text-xs text-gray-500">{u.email}</div>
      </td>
      <td className="px-4 py-2.5">
        <span className={`inline-block px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${ROLE_BADGE[u.role] || "bg-gray-100"}`}>{u.role}</span>
      </td>
      <td className="px-4 py-2.5" data-testid={`user-access-${u.id}`}>
        {u.role === "admin" ? (
          <span className="text-xs text-gray-500">All projects · Both</span>
        ) : (
          <div className="text-xs text-gray-600 leading-tight">
            <div>{u.project_access === "restricted" ? `${(u.allowed_projects || []).length} project${(u.allowed_projects || []).length === 1 ? "" : "s"}` : "All projects"}</div>
            <div className="text-[11px] text-gray-400 capitalize">{u.resource_scope === "assets" ? "Assets only" : u.resource_scope === "workers" ? "Workers only" : "Both"}</div>
          </div>
        )}
      </td>
      <td className="px-4 py-2.5">
        <span className={`inline-block px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${STATUS_BADGE[u.status] || "bg-gray-100"}`} data-testid={`user-status-${u.id}`}>{u.status}</span>
      </td>
      <td className="px-4 py-2.5 text-xs text-gray-500">{fmt(u.last_login)}</td>
      <td className="px-4 py-2.5 text-xs text-gray-500">{fmt(u.created_at)}</td>
      <td className="px-4 py-2.5 text-right relative">
        <button onClick={() => setMenu((m) => !m)} data-testid={`user-menu-${u.id}`} className="text-gray-400 hover:text-equipr-black">
          <DotsThreeVertical size={18} weight="bold" />
        </button>
        {menu && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setMenu(false)} />
            <div className="absolute right-2 top-8 z-20 bg-white border border-gray-300 shadow-lg w-52 text-left text-sm">
              <button onClick={() => { setMenu(false); onEdit(u); }} data-testid={`user-edit-${u.id}`} className="w-full text-left px-3 py-2 hover:bg-gray-100 flex items-center gap-2"><PencilSimple size={15} /> Edit name & role</button>
              <button onClick={() => { setMenu(false); onReset(u); }} data-testid={`user-reset-${u.id}`} className="w-full text-left px-3 py-2 hover:bg-gray-100 flex items-center gap-2"><Key size={15} /> Send password reset</button>
              {u.status === "inactive" ? (
                <button onClick={() => { setMenu(false); onStatus(u, "active"); }} data-testid={`user-activate-${u.id}`} className="w-full text-left px-3 py-2 hover:bg-gray-100 flex items-center gap-2 text-green-700"><CheckCircle size={15} /> Activate</button>
              ) : (
                <button onClick={() => { setMenu(false); onStatus(u, "inactive"); }} disabled={isSelf} data-testid={`user-deactivate-${u.id}`} className="w-full text-left px-3 py-2 hover:bg-gray-100 flex items-center gap-2 text-amber-700 disabled:opacity-40 disabled:hover:bg-white"><Prohibit size={15} /> Deactivate</button>
              )}
              <button onClick={() => { setMenu(false); onDelete(u); }} disabled={isSelf} data-testid={`user-delete-${u.id}`} className="w-full text-left px-3 py-2 hover:bg-red-50 text-red-600 border-t border-gray-100 flex items-center gap-2 disabled:opacity-40 disabled:hover:bg-white"><Trash size={15} /> Delete</button>
            </div>
          </>
        )}
      </td>
    </tr>
  );
}

/* ------------------------------ Users section ----------------------------- */
function UsersSection() {
  const { user: self } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [modal, setModal] = useState(null);      // {mode, user}
  const [confirm, setConfirm] = useState(null);   // config
  const [link, setLink] = useState(null);         // link modal config
  const [projects, setProjects] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/users"); setUsers(data); } catch {}
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {}); }, []);

  const filtered = users.filter((u) => {
    const t = q.toLowerCase();
    const matchQ = !t || (u.name || "").toLowerCase().includes(t) || u.email.toLowerCase().includes(t);
    const matchR = !roleFilter || u.role === roleFilter;
    return matchQ && matchR;
  });

  const doReset = async (u) => {
    const { data } = await api.post(`/users/${u.id}/reset-password`);
    setLink({ title: "Password reset", link: data.reset_link, emailSent: data.email_sent, email: u.email });
  };
  const doStatus = (u, status) => {
    setConfirm({
      title: status === "inactive" ? "Deactivate user?" : "Activate user?",
      message: status === "inactive"
        ? `${u.name || u.email} will no longer be able to sign in until reactivated.`
        : `${u.name || u.email} will be able to sign in again.`,
      confirmLabel: status === "inactive" ? "Deactivate" : "Activate",
      danger: status === "inactive",
      onConfirm: async () => { await api.patch(`/users/${u.id}/status`, { status }); setConfirm(null); load(); },
    });
  };
  const doDelete = (u) => {
    setConfirm({
      title: "Delete user?",
      message: `This permanently removes ${u.name || u.email}. This cannot be undone.`,
      confirmLabel: "Delete", danger: true,
      onConfirm: async () => { await api.delete(`/users/${u.id}`); setConfirm(null); load(); },
    });
  };

  return (
    <div data-testid="users-section">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" data-testid="user-search" className={inp + " max-w-xs"} />
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} data-testid="user-role-filter" className={inp + " max-w-[160px]"}>
          <option value="">All roles</option>
          <option value="admin">Admin</option>
          <option value="scheduler">Scheduler</option>
          <option value="viewer">Viewer</option>
        </select>
        <div className="ml-auto">
          <button onClick={() => setModal({ mode: "invite" })} data-testid="invite-user-button"
            className="h-9 px-4 bg-equipr-yellow text-equipr-black text-[12px] uppercase tracking-[0.04em] font-bold rounded-[7px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px flex items-center gap-1.5">
            <Plus size={16} weight="bold" /> Invite User
          </button>
        </div>
      </div>

      <div className="border border-border rounded-[10px] overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-surface-sunken text-ink-tertiary text-left text-colhead uppercase border-b border-border-warm">
              <th className="px-4 py-2.5 font-semibold">User</th>
              <th className="px-4 py-2.5 font-semibold">Role</th>
              <th className="px-4 py-2.5 font-semibold">Access</th>
              <th className="px-4 py-2.5 font-semibold">Status</th>
              <th className="px-4 py-2.5 font-semibold">Last Login</th>
              <th className="px-4 py-2.5 font-semibold">Created</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-400">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-400" data-testid="users-empty">No users found.</td></tr>
            ) : filtered.map((u) => (
              <UserRow key={u.id} u={u} self={self} onEdit={(x) => setModal({ mode: "edit", user: x })}
                onReset={doReset} onStatus={doStatus} onDelete={doDelete} />
            ))}
          </tbody>
        </table>
      </div>

      {modal && <UserModal {...modal} projects={projects} onClose={() => setModal(null)} onDone={load} onLink={setLink} />}
      {confirm && <ConfirmModal {...confirm} onCancel={() => setConfirm(null)} />}
      {link && <LinkModal {...link} onClose={() => setLink(null)} />}
    </div>
  );
}

/* ------------------------ Company & System sections ---------------------- */
function useSettings() {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { const r = await api.get("/settings"); setData(r.data); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);
  return [data, setData, load];
}

function CompanySection() {
  const [settings, , reload] = useSettings();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  useEffect(() => { if (settings) setForm(settings.company); }, [settings]);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const onLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 500 * 1024) { setError("Logo must be under 500KB."); return; }
    const reader = new FileReader();
    reader.onload = () => set("logo", reader.result);
    reader.readAsDataURL(f);
  };

  const save = async () => {
    setSaving(true); setMsg(""); setError("");
    try { await api.put("/settings", { company: form }); setMsg("Company details saved."); reload(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || "Save failed."); }
    setSaving(false);
  };

  if (!settings) return <p className="text-sm text-gray-400">Loading…</p>;
  const FIELDS = [
    ["name", "Company Name", true], ["trading_name", "Trading Name"], ["abn", "ABN / ACN"],
    ["phone", "Phone"], ["email", "Email"], ["website", "Website"],
  ];
  return (
    <div className="max-w-2xl" data-testid="company-section">
      {msg && <div className="mb-3 border border-green-300 bg-green-50 text-green-700 text-sm px-3 py-2" data-testid="settings-saved">{msg}</div>}
      {error && <div className="mb-3 border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}
      <div className="flex items-start gap-6 mb-6">
        <div className="w-32 h-32 border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden shrink-0">
          {form.logo ? <img src={form.logo} alt="logo" className="object-contain w-full h-full" data-testid="company-logo-preview" /> : <span className="text-xs text-gray-400 text-center px-2">No logo</span>}
        </div>
        <div>
          <label className={lbl}>Company Logo</label>
          <input type="file" accept="image/*" onChange={onLogo} data-testid="company-logo-input" className="text-sm" />
          <p className="text-[11px] text-gray-500 mt-1">PNG or JPG, under 500KB. Used on printed summaries.</p>
          {form.logo && <button onClick={() => set("logo", "")} className="text-xs text-red-600 mt-1 hover:underline" data-testid="company-logo-remove">Remove logo</button>}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        {FIELDS.map(([k, l, req]) => (
          <div key={k}>
            <label className={lbl}>{l}{req ? " *" : ""}</label>
            <input className={inp} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`company-${k}`} />
          </div>
        ))}
        <div className="col-span-2">
          <label className={lbl}>Address</label>
          <textarea className={inp + " h-20 py-1"} value={form.address || ""} onChange={(e) => set("address", e.target.value)} data-testid="company-address" />
        </div>
      </div>
      <div className="mt-5">
        <button onClick={save} disabled={saving} data-testid="company-save"
          className="h-9 px-5 bg-equipr-yellow text-equipr-black text-[12px] uppercase tracking-[0.04em] font-bold rounded-[7px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px disabled:opacity-60">
          {saving ? "Saving…" : "Save Company Details"}
        </button>
      </div>
    </div>
  );
}

function SystemSection() {
  const [settings, , reload] = useSettings();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  useEffect(() => { if (settings) setForm(settings.system); }, [settings]);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setSaving(true); setMsg("");
    try {
      await api.put("/settings", { system: { ...form, gst_percent: Number(form.gst_percent) } });
      setMsg("System rules saved."); reload();
    } catch {}
    setSaving(false);
  };
  if (!settings) return <p className="text-sm text-gray-400">Loading…</p>;
  return (
    <div className="max-w-lg" data-testid="system-section">
      {msg && <div className="mb-3 border border-green-300 bg-green-50 text-green-700 text-sm px-3 py-2" data-testid="settings-saved">{msg}</div>}
      <div className="space-y-4">
        <div>
          <label className={lbl}>GST Percentage (used on Weekly Hire Summary)</label>
          <div className="flex items-center gap-2">
            <input type="number" step="0.5" className={inp + " max-w-[120px]"} value={form.gst_percent ?? 10} onChange={(e) => set("gst_percent", e.target.value)} data-testid="system-gst" />
            <span className="text-sm text-gray-500">%</span>
          </div>
        </div>
        <div>
          <label className={lbl}>Default Hire Rate Type</label>
          <select className={inp} value={form.default_rate_type || "Hourly"} onChange={(e) => set("default_rate_type", e.target.value)} data-testid="system-rate-type">
            {["Hourly", "Daily", "Weekly", "Monthly"].map((r) => <option key={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label className={lbl}>Week Starts On</label>
          <select className={inp} value={form.week_start || "Monday"} onChange={(e) => set("week_start", e.target.value)} data-testid="system-week-start">
            {["Monday", "Sunday"].map((r) => <option key={r}>{r}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input type="checkbox" checked={!!form.auto_standdown} onChange={(e) => set("auto_standdown", e.target.checked)} data-testid="system-auto-standdown" className="w-4 h-4 accent-equipr-yellow" />
          Auto stand-down on weekends & public holidays (default for new projects)
        </label>
      </div>
      <div className="mt-5">
        <button onClick={save} disabled={saving} data-testid="system-save"
          className="h-9 px-5 bg-equipr-yellow text-equipr-black text-[12px] uppercase tracking-[0.04em] font-bold rounded-[7px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px disabled:opacity-60">
          {saving ? "Saving…" : "Save System Rules"}
        </button>
      </div>
    </div>
  );
}

/* --------------------------- Notifications section ------------------------ */
const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const WINDOWS = [30, 60, 90];

function NotificationsSection() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    api.get("/digest-settings").then(({ data }) => setForm(data)).catch(() => setForm({ enabled: true, day: "Monday", window: 30 }));
  }, []);

  const save = async () => {
    setSaving(true); setMsg(""); setError("");
    try {
      const { data } = await api.put("/digest-settings", { enabled: !!form.enabled, day: form.day, window: Number(form.window) });
      setForm(data); setMsg("Notification preferences saved.");
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || "Save failed."); }
    setSaving(false);
  };

  const sendNow = async () => {
    setSending(true); setMsg(""); setError("");
    try {
      const { data } = await api.post("/compliance/send-digest");
      setMsg(data.sent ? `Digest sent — ${data.items} item(s) within ${data.window} days.` : `Nothing to send right now (${data.reason || "no expiring items"}).`);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not send digest."); }
    setSending(false);
  };

  if (!form) return <p className="text-sm text-gray-400">Loading…</p>;
  return (
    <div className="max-w-lg" data-testid="notifications-section">
      {msg && <div className="mb-3 border border-green-300 bg-green-50 text-green-700 text-sm px-3 py-2" data-testid="notifications-saved">{msg}</div>}
      {error && <div className="mb-3 border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2" data-testid="notifications-error">{error}</div>}

      <p className="text-sm text-gray-600 mb-4">Get a weekly email listing worker licences, tickets and inductions that are expired or coming up for renewal. These preferences apply to your account only.</p>

      <div className="space-y-4">
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input type="checkbox" checked={!!form.enabled} onChange={(e) => set("enabled", e.target.checked)} data-testid="digest-enabled" className="w-4 h-4 accent-equipr-yellow" />
          Email me the compliance expiry digest
        </label>

        <div className={form.enabled ? "" : "opacity-40 pointer-events-none"}>
          <div className="mb-4">
            <label className={lbl}>Send on</label>
            <select className={inp} value={form.day} onChange={(e) => set("day", e.target.value)} data-testid="digest-day">
              {WEEKDAYS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>Warning window</label>
            <select className={inp} value={form.window} onChange={(e) => set("window", Number(e.target.value))} data-testid="digest-window">
              {WINDOWS.map((w) => <option key={w} value={w}>Next {w} days</option>)}
            </select>
            <p className="text-[11px] text-gray-500 mt-1">Includes items expiring within this window (plus anything already expired).</p>
          </div>
        </div>
      </div>

      <div className="mt-5 flex items-center gap-2">
        <button onClick={save} disabled={saving} data-testid="digest-save"
          className="h-9 px-5 bg-equipr-yellow text-equipr-black text-[12px] uppercase tracking-[0.04em] font-bold rounded-[7px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px disabled:opacity-60">
          {saving ? "Saving…" : "Save Preferences"}
        </button>
        <button onClick={sendNow} disabled={sending} data-testid="digest-send-now"
          className="h-9 px-4 border border-border-control bg-white text-equipr-black text-[12px] uppercase tracking-[0.04em] font-semibold rounded-[7px] hover:bg-surface-panel active:bg-surface-pressed disabled:opacity-60">
          {sending ? "Sending…" : "Send me one now"}
        </button>
      </div>
    </div>
  );
}

/* -------------------------------- Page ------------------------------------ */
const TABS = [
  { key: "users", label: "Users", icon: UsersThree, desc: "Invite teammates and control what each person can see and edit." },
  { key: "company", label: "Company", icon: Buildings, desc: "Business details and logo used on printed summaries." },
  { key: "system", label: "System Rules", icon: Gear, desc: "Defaults for rates, GST and the working week." },
  { key: "notifications", label: "Notifications", icon: Bell, desc: "Your personal compliance expiry email digest." },
];

export default function Settings() {
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState("users");
  if (!isAdmin) return <Navigate to="/" replace />;
  const meta = TABS.find((t) => t.key === tab);
  return (
    <div className="h-full overflow-y-auto bg-surface-page" data-testid="settings-page">
      <div className="px-6 pt-6 pb-8 max-w-[1180px]">
        <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-ink-muted">System</div>
        <h1 className="font-heading text-[26px] font-bold tracking-tight text-ink-primary mt-1">Settings</h1>
        <p className="text-[13px] text-ink-tertiary mt-1">Manage users, company details and system rules for your organisation.</p>

        <div className="mt-5 flex gap-5 items-start">
          <aside className="w-[248px] shrink-0">
            <div className="bg-surface-card border border-border rounded-[12px] shadow-card overflow-hidden">
              <div className="px-4 pt-3 pb-1.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-ink-muted">Settings</div>
              <nav className="pb-2">
                {TABS.map((t) => {
                  const active = tab === t.key;
                  return (
                    <button key={t.key} onClick={() => setTab(t.key)} data-testid={`settings-tab-${t.key}`}
                      className={`w-full flex items-center gap-2.5 pl-4 pr-3 py-2.5 border-l-[3px] text-left transition-colors ${
                        active ? "border-equipr-yellow bg-accent-tint" : "border-transparent hover:bg-surface-panel"}`}>
                      <t.icon size={17} weight={active ? "fill" : "regular"} className={active ? "text-ink-primary" : "text-ink-tertiary"} />
                      <span className={`text-[13.5px] ${active ? "font-bold text-ink-primary" : "font-medium text-ink-secondary"}`}>{t.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </aside>

          <section className="flex-1 min-w-0">
            <div className="bg-surface-card border border-border rounded-[12px] shadow-card overflow-hidden">
              <div className="px-5 py-3.5 border-b border-divider-inner">
                <h2 className="font-heading text-[16px] font-bold tracking-tight text-ink-primary">{meta.label}</h2>
                <p className="text-[12px] text-ink-tertiary mt-0.5">{meta.desc}</p>
              </div>
              <div className="p-5">
                {tab === "users" && <UsersSection />}
                {tab === "company" && <CompanySection />}
                {tab === "system" && <SystemSection />}
                {tab === "notifications" && <NotificationsSection />}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
